import { Injectable } from '@angular/core';
import { AuthService } from './auth/auth.service';
import { PermissionsEnum } from '@generated/permissions.enum';
import { Permissions, PermissionScope, RoleEntityContextInput } from '@generated/graphql';
import { DebugService } from './debug/debug.service';
import { fluxLogger } from './logger/logger.instance';

type PermissionResolvable = PermissionsEnum | `${PermissionsEnum}`;

const logger = fluxLogger.extend('permissions');

@Injectable({
  providedIn: 'root',
})
export class PermissionsService {
  constructor(
    private readonly authService: AuthService,
    private readonly debugService: DebugService,
  ) {}

  public hasPermission(
    permission: PermissionResolvable,
    roleEntityContext?: RoleEntityContextInput,
  ): boolean {
    const debugOverridePermissions =
      this.debugService.getSingleDebugMetadata<string[]>('overridedPermissions');
    if (debugOverridePermissions) {
      const hasPermission = debugOverridePermissions.includes(permission);
      logger.warn(`Permission ${permission} overrided by debug`, {
        hasPermission,
      });
      return hasPermission;
    }

    if (!this.authService.isAuthenticated) {
      logger.debug(`Permission ${permission} not granted (not authenticated)`);
      return false;
    }

    if (!roleEntityContext?.databaseId && !roleEntityContext?.pipeId) {
      logger.debug(`Permission ${permission} not granted (no role entity context)`);
      return this.authService.authenticatedUserPermissions.has(permission);
    }

    if (roleEntityContext?.databaseId) {
      const databaseResolvedRole = this.authService.databaseRolePermissions(
        roleEntityContext.databaseId,
      );
      if (!databaseResolvedRole) return false;

      logger.debug(`Permission ${permission} granted (database role)`, {
        databaseResolvedRole,
      });
      return databaseResolvedRole.has(permission);
    }

    const pipeResolvedRole = this.authService.pipeRolePermissions(roleEntityContext?.pipeId ?? '');
    if (!pipeResolvedRole) return false;

    logger.debug(`Permission ${permission} granted (pipe role)`, {
      pipeResolvedRole,
    });
    return pipeResolvedRole.has(permission);
  }

  public hasAnyPermission(
    permissions: PermissionResolvable[],
    roleEntityContext: RoleEntityContextInput,
  ): boolean {
    const result = permissions.some((permission) =>
      this.hasPermission(permission, roleEntityContext),
    );
    logger.debug('Checking permissions (hasAnyPermission)', {
      permissions,
      roleEntityContext,
      result,
    });
    return result;
  }

  public hasAllPermissions(
    permissions: PermissionResolvable[],
    roleEntityContext: RoleEntityContextInput,
  ): boolean {
    const result = permissions.every((permission) =>
      this.hasPermission(permission, roleEntityContext),
    );
    logger.debug('Checking permissions (hasAllPermissions)', {
      permissions,
      roleEntityContext,
      result,
    });
    return result;
  }

  public static overrideGlobalPermissionsWithScopedPermissions(
    globalPermissions: Permissions[],
    scopedPermissions: Permissions[],
  ): Map<string, Permissions> {
    if (!globalPermissions?.length && !scopedPermissions?.length) return new Map();

    if (!scopedPermissions?.length)
      return new Map(globalPermissions.map((permission) => [permission.id, permission]));
    if (!globalPermissions?.length)
      return new Map(scopedPermissions.map((permission) => [permission.id, permission]));

    const globalPermissionsWithoutLocal = globalPermissions.filter(
      (permission) =>
        permission.scope !== PermissionScope.Pipes &&
        permission.scope !== PermissionScope.Databases,
    );

    return new Map(
      [...globalPermissionsWithoutLocal, ...scopedPermissions].map((permission) => [
        permission.id,
        permission,
      ]),
    );
  }
}
